import React from "react";
import MUIDataTable from "mui-datatables";
import { Typography, Box, IconButton } from "@mui/material";
import { PhotoProvider, PhotoView } from "react-photo-view";
import DownloadIcon from "@mui/icons-material/Download";
import { saveAs } from "file-saver";
import BoltIcon from "@mui/icons-material/Bolt";
import { COLORS } from "@mui/x-data-grid-generator/services/static-data";

function OrderListTable({ productList = [], addonList = [] }) {
  const handleDownload = async (url, filename = "image.jpg") => {
    try {
      const res = await fetch(url, { mode: "cors" });
      const blob = await res.blob();
      saveAs(blob, filename);
    } catch {
      alert("Download failed");
    }
  };

  const seenAddonIds = new Set();

  const uniqueAddons = (addonList || []).filter((addon) => {
    if (addon.parent_id !== null && addon.parent_id !== undefined) return false; // ✅ skip if has parent_id
    const id = String(addon?.addon_id);
    if (seenAddonIds.has(id)) return false;
    seenAddonIds.add(id);
    return true;
  });
  const isCombo = (row) => row?.combo_price > 0;

  /** ---------- Combine products + addons ---------- */
  const combinedDataRaw = [
    ...productList,
    ...uniqueAddons.map((addon) => ({ ...addon, isAddon: true })),
  ];

  const combosByParentId = {};
  const normalProducts = [];
  const includedComboParentIds = new Set();

  combinedDataRaw.forEach((item) => {
    const parentDetails = item.parent_product_details?.[0];

    if (parentDetails) {
      const parentId = parentDetails.id;

      if (!includedComboParentIds.has(parentId)) {
        includedComboParentIds.add(parentId);

        normalProducts.push({
          id: parentId,
          product_name: parentDetails.product_name,
          product_price: parentDetails.combo_price,
          product_discount_price:
            item.product_discount_price || parentDetails.combo_price,
          image:
            parentDetails.images?.[0]?.app_product_image ||
            parentDetails.images?.[0]?.product_image ||
            null,
          product_quantity:
            item.product_quantity ||
            item.quantity ||
            parentDetails.product_quantity ||
            1,
          product_total:
            item.product_total || item.total || parentDetails.combo_price,
          isComboGroup: true,
          combo_price: parentDetails.combo_price || 0,
          egg_or_eggless: parentDetails.egg_or_eggless,
          combo_product_names:
            parentDetails.combo_products || item.combo_product_names,
          combos: [],
          comboParent: {
            ...parentDetails,
            product_quantity:
              item.product_quantity ||
              item.quantity ||
              parentDetails.product_quantity ||
              1,
          },
        });
      }

      const comboGroupIndex = normalProducts.findIndex(
        (p) => p.id === parentId && p.isComboGroup,
      );
      if (comboGroupIndex !== -1) {
        normalProducts[comboGroupIndex].combos.push({
          ...item,
          isComboChild: true,
          parent_combo_id: parentId,
        });
      }
    } else if (item.combo_price > 0) {
      combosByParentId[item.id] = combosByParentId[item.id] || {
        comboParent: item,
        combos: [],
      };
    } else {
      normalProducts.push(item);
    }
  });

  const comboGroups = Object.values(combosByParentId)
    .filter(
      (g) =>
        g.comboParent && g.comboParent.combo_price > 0 && g.combos.length > 0,
    )
    .map((g) => ({
      ...g.comboParent,
      isComboGroup: true,
      combos: g.combos,
      comboParent: {
        ...g.comboParent,
        product_quantity:
          g.comboParent.product_quantity || g.comboParent.quantity || 1,
      },
    }));

  const combinedData = [...normalProducts, ...comboGroups];

  /** ---------- Table Columns ---------- */
  const columns = [
    {
      name: "serial",
      label: "S. No",
      options: { customBodyRenderLite: (i) => i + 1 },
    },
    {
      name: "image",
      label: "Image",
      options: {
        customBodyRenderLite: (i) => {
          const row = combinedData[i];
          const dataRow = row.isComboGroup ? row.comboParent : row;
          const url =
            dataRow?.product_details?.[0]?.images?.[0]?.app_product_image ||
            dataRow?.image ||
            dataRow?.images?.[0]?.app_product_image;

          return url ? (
            <PhotoProvider>
              <PhotoView src={url}>
                <img
                  src={url}
                  alt=""
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                />
              </PhotoView>
            </PhotoProvider>
          ) : (
            "-"
          );
        },
      },
    },
    // {
    //   name: "product_name",
    //   label: "Name",
    //   options: {
    //     customBodyRenderLite: (i) => {
    //       const row = combinedData[i];
    //       if (row.isComboGroup && row.combos?.length > 0) {
    //         const parent = row.comboParent;
    //         const eggless = parent?.egg_or_eggless === 1;

    //         return (
    //           <div>
    //             <span>
    //               {parent.product_name}
    //               {eggless && <span style={{ color: "red" }}> (Eggless)</span>}
    //             </span>
    //             <span
    //               style={{
    //                 marginLeft: 8,
    //                 backgroundColor: "#dc3545",
    //                 color: "white",
    //                 fontSize: "0.75rem",
    //                 fontWeight: "bold",
    //                 padding: "2px 8px",
    //                 borderRadius: "4px",
    //                 textTransform: "uppercase",
    //               }}
    //             >
    //               COMBO
    //             </span>
    //           </div>
    //         );
    //       }

    //       const name =
    //         row?.product_details?.[0]?.product_name || row?.product_name;
    //       return (
    //         <div>
    //           {name}
    //           {row?.egg_or_eggless === 1 && (
    //             <span style={{ color: "red" }}> (Eggless)</span>
    //           )}
    //         </div>
    //       );
    //     },
    //   },
    // },
    {
      name: "product_name",
      label: "Name",
      options: {
        customBodyRenderLite: (i) => {
          const row = combinedData[i];

          if (row.isComboGroup && row.combos?.length > 0) {
            const parent = row.comboParent;
            const eggless = parent?.egg_or_eggless === 1;
            const fastDelivery =
              Number(parent?.fast_delivery) === 1 ||
              Number(parent?.product_details?.[0]?.fast_delivery) === 1 ||
              parent?.variation?.egg_preparation === "1" ||
              parent?.variation?.eggless_preparation === "1";
            const isBulk = Number(row?.is_bulk_product) > 0;
          const isFreeProduct =
            Number(row?.is_offer_reward) === 1 ||
            Boolean(row?.is_free_product) ||
            row?.free_tag === "FREE" ||
            (!row?.isAddon &&
              Number(row?.combo_price || 0) === 0 &&
              Number(row?.product_total) === 0 &&
              Number(row?.product_quantity) > 0);

            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center", // ✅ center horizontally
                  alignItems: "center",
                  gap: 6,
                  width: "100%", // ✅ VERY IMPORTANT
                  textAlign: "center", // ✅ fallback
                }}
              >
                <span>
                  {parent.product_name}
                  {eggless && <span style={{ color: "red" }}> (Eggless)</span>}
                </span>

                <span
                  style={{
                    marginLeft: 8,
                    backgroundColor: "#dc3545",
                    color: "white",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    padding: "2px 8px",
                    borderRadius: "4px",
                  }}
                >
                  COMBO
                </span>
                {isBulk && (
                  <span
                    style={{
                      backgroundColor: "#e91e63",
                      color: "white",
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      padding: "2px 8px",
                      borderRadius: "4px",
                    }}
                  >
                    BULK
                  </span>
                )}

                {fastDelivery && (
                  <span
                    title="Fast Delivery"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      background: "linear-gradient(135deg, #FF6B00, #FFB347)",
                      color: "white",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: "12px",
                      gap: 3,
                    }}
                  >
                    <BoltIcon sx={{ fontSize: 14, color: "white" }} />
                  </span>
                )}
              </div>
            );
          }

          const name =
            row?.product_details?.[0]?.product_name || row?.product_name;

          const fastDelivery =
            Number(row?.fast_delivery) === 1 ||
            Number(row?.product_details?.[0]?.fast_delivery) === 1 ||
            row?.variation?.egg_preparation === "1" ||
            row?.variation?.eggless_preparation === "1";
          const isBulk = Number(row?.is_bulk_product) > 0;
          const isFreeProduct =
            Number(row?.is_offer_reward) === 1 ||
            Boolean(row?.is_free_product) ||
            row?.free_tag === "FREE" ||
            (!row?.isAddon &&
              Number(row?.combo_price || 0) === 0 &&
              Number(row?.product_total) === 0 &&
              Number(row?.product_quantity) > 0);

          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center", // ✅ center
                alignItems: "center",
                gap: 6,
                width: "100%", // ✅ important
                textAlign: "center",
              }}
            >
              <span>
                {name}
                {row?.egg_or_eggless === 1 && (
                  <span style={{ color: "red" }}> (Eggless)</span>
                )}
              </span>
              {isBulk && ( // ✅ ADD
                <span
                  title="Bulk Product"
                  style={{
                    backgroundColor: "#e91e63",
                    color: "white",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    padding: "2px 8px",
                    borderRadius: "4px",
                  }}
                >
                  BULK
                </span>
              )}

              {fastDelivery && (
                <span
                  title="Fast Delivery"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    // background: "linear-gradient(135deg, #FF6B00, #FFB347)",
                    color: "white",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    padding: "2px 8px",
                    borderRadius: "12px",
                    gap: 3,
                  }}
                >
                  <BoltIcon
                    sx={{ fontSize: 26, color: "#FF6B00", rotate: "15deg" }}
                  />
                </span>
              )}
              {isFreeProduct && (
                <span
                  title="Free Product"
                  className="free-tag"
                  style={{
                    backgroundColor: "#e5232a",
                    color: "white",
                    fontSize: "0.7rem",
                    fontWeight: "bold",
                    padding: "2px 8px",
                    borderRadius: "4px",
                  }}
                >
                  FREE
                </span>
              )}
            </div>
          );
        },
      },
    },
    {
      name: "photo",
      label: "Photo",
      options: {
        display: productList.some(
          (p) => p?.product_details?.[0]?.photo_cakes_images?.length > 0,
        ),
        customBodyRenderLite: (i) => {
          const row = combinedData[i];
          const dataRow = row.isComboGroup ? row.comboParent : row;
          const img =
            dataRow?.product_details?.[0]?.photo_cakes_images?.[0]
              ?.photo_cake_image;

          return img ? (
            <Box position="relative" display="inline-block">
              <PhotoProvider>
                <PhotoView src={img}>
                  <img
                    src={img}
                    alt=""
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                  />
                </PhotoView>
              </PhotoProvider>
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  bottom: -6,
                  right: -6,
                  backgroundColor: "#fff",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
                onClick={() => handleDownload(img, `Photo-cake-${i + 1}.jpg`)}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            "-"
          );
        },
      },
    },
    {
      name: "weight",
      label: "Weight",
      options: {
        customBodyRenderLite: (i) => {
          const row = combinedData[i];
          const dataRow = row.isComboGroup ? row.comboParent : row;
          if (dataRow?.variation?.weight?.[0]) {
            const w = dataRow.variation.weight[0];
            return `${w.weight_name} ${w.unit.unit_name}`;
          }
          return "-";
        },
      },
    },
    {
      name: "flavour",
      label: "Flavour",
      options: {
        customBodyRenderLite: (i) => {
          const row = combinedData[i];
          const dataRow = row.isComboGroup ? row.comboParent : row;
          const f = dataRow?.flavour?.flavour_name;
          return f && f !== "Default" ? f : "-";
        },
      },
    },
    {
      name: "message_on_cake",
      label: "Message",
      options: {
        customBodyRenderLite: (i) => {
          const row = combinedData[i];
          const dataRow = row.isComboGroup ? row.comboParent : row;
          return dataRow?.message_on_cake || "-";
        },
      },
    },

    {
      name: "price",
      label: "Price",
      options: {
        customBodyRenderLite: (i) => {
          const row = combinedData[i];
          const dataRow = row.isComboGroup ? row.comboParent : row;
          const price = dataRow?.product_price || dataRow?.original_price || dataRow?.price || 0;
          const discountPrice = dataRow?.product_discount_price || 0;

          const hasCombo = dataRow?.combo_price > 0;
          const hasDiscount = discountPrice > 0 && discountPrice < price;

          return hasCombo || hasDiscount ? (
            <span style={{ textDecoration: "line-through", color: "red" }}>
              ₹ {price}
            </span>
          ) : (
            `₹ ${price}`
          );
        },
      },
    },
    {
      name: "combo",
      label: "Offer Price",
      options: {
        display:
          combinedData.some(isCombo) ||
          combinedData.some((row) => {
            const dataRow = row.isComboGroup ? row.comboParent : row;
            const price = dataRow?.product_price || dataRow?.original_price || dataRow?.price || 0;
            return (
              (dataRow?.product_discount_price || 0) > 0 &&
              dataRow.product_discount_price < price
            );
          }),
        customBodyRenderLite: (i) => {
          const row = combinedData[i];
          const dataRow = row.isComboGroup ? row.comboParent : row;
          const comboPrice = dataRow?.combo_price || 0;
          const price = dataRow?.product_price || dataRow?.original_price || dataRow?.price || 0;
          const discountPrice = dataRow?.product_discount_price || 0;

          if (comboPrice > 0) {
            return (
              <span style={{ fontWeight: "bold", color: "#28a745" }}>
                ₹ {comboPrice} <small style={{ color: "#888" }}>(Combo)</small>
              </span>
            );
          }

          if (discountPrice > 0 && discountPrice < price) {
            return (
              <span style={{ fontWeight: "bold", color: "#28a745" }}>
                ₹ {discountPrice} <small style={{ color: "#888" }}>(Discount)</small>
              </span>
            );
          }

          return "-";
        },
      },
    },
    {
      name: "quantity",
      label: "Qty",
      options: {
        customBodyRenderLite: (i) => {
          const row = combinedData[i];
          return row?.product_quantity || row?.quantity || "-";
        },
      },
    },

    {
      name: "total",
      label: "Total",
      options: {
        customBodyRenderLite: (i) => {
          const row = combinedData[i];
          const qty = row.product_quantity || row.quantity || 1;
          const comboPrice = row.combo_price || 0;

          // ✅ Combo — no promo applies
          if (comboPrice > 0) {
            return `₹ ${comboPrice * qty}`;
          }

          const total = row.product_total || row.total || 0;
          const bestTotal = row.best_product_total || null;
          const promoPercent = row.promo_code_percentage || null;

          // ✅ Promo applied — show ONLY best price (no strike)
          if (promoPercent && bestTotal) {
            return `₹ ${bestTotal}`;
          }

          // ✅ No promo — show normal price
          return `₹ ${total}`;
        },
      },
    },
  ];

  const options = {
    selectableRows: false,
    filter: false,
    print: false,
    download: false,
    viewColumns: true,
    search: false,
    rowsPerPage: 100,

    expandableRows: true,
    expandableRowsOnClick: true,
    expandableRowsHideExpander: false,

    isRowExpandable: (dataIndex) => {
      const row = combinedData[dataIndex];
      return (
        row?.isComboGroup === true &&
        Array.isArray(row.combos) &&
        row.combos.length > 0
      );
    },

    setRowProps: (row, dataIndex) => {
      const rowData = combinedData[dataIndex];
      return {
        className: rowData?.isComboGroup ? "combo-row" : "normal-row",
      };
    },

    renderExpandableRow: (rowData, rowMeta) => {
      const parentRow = combinedData[rowMeta.dataIndex];
      if (!parentRow.isComboGroup || !parentRow.combos?.length) return null;

      return (
        <tr>
          <td colSpan={columns.length + 1}>
            <Box sx={{ p: 2, backgroundColor: "#fafafa" }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", mb: 2 }}
              >
                Combo Items ({parentRow.combos.length})
              </Typography>
              <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fit, minmax(260px, 1fr))"
                gap={1.5}
              >
                {parentRow.combos.map((c, idx) => {
                  const img =
                    c?.product_details?.[0]?.images?.[0]?.app_product_image ||
                    c?.image ||
                    c?.images?.[0]?.app_product_image;
                  const name =
                    c?.product_details?.[0]?.product_name || c?.product_name;

                  return (
                    <Box
                      key={idx}
                      sx={{
                        border: "1px solid #ddd",
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        {img ? (
                          <PhotoProvider>
                            <PhotoView src={img}>
                              <img
                                src={img}
                                alt={name}
                                style={{
                                  width: 60,
                                  height: 60,
                                  borderRadius: 8,
                                  marginRight: 12,
                                }}
                              />
                            </PhotoView>
                          </PhotoProvider>
                        ) : (
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              bgcolor: "action.hover",
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mr: 1.5,
                            }}
                          >
                            <Typography variant="caption">No Image</Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, fontSize: "0.95rem" }}
                          >
                            {name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {c.product_quantity || c.quantity} Pack
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            ₹ {c.product_price || c.price || 0}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
                {/* 👇 Add this after */}
                {(parentRow.combos?.[0]?.combo_addon_details || []).map(
                  (addon, idx) => (
                    <Box
                      key={`addon-${idx}`}
                      sx={{
                        border: "1px solid #ddd",
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        {addon.image ? (
                          <PhotoProvider>
                            <PhotoView src={addon.image}>
                              <img
                                src={addon.image}
                                alt={addon.product_name}
                                style={{
                                  width: 60,
                                  height: 60,
                                  borderRadius: 8,
                                  marginRight: 12,
                                }}
                              />
                            </PhotoView>
                          </PhotoProvider>
                        ) : (
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              bgcolor: "action.hover",
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mr: 1.5,
                            }}
                          >
                            <Typography variant="caption">No Image</Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, fontSize: "0.95rem" }}
                          >
                            {addon.product_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Add-on
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            ₹ {addon.price}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ),
                )}
              </Box>
            </Box>
          </td>
        </tr>
      );
    },
  };

  return (
    <>
      <h5 className="text-primary">Product Details</h5>
      <Box
        sx={{
          ".normal-row  #expandable-button": {
            display: "none",
          },
          ".normal-row td:first-child": {
            paddingLeft: 0,
          },
        }}
      >
        <MUIDataTable
          data={combinedData}
          columns={columns}
          options={options}
          title=""
        />
      </Box>
    </>
  );
}

export default OrderListTable;
